import React, { useState, useEffect } from "react";
import { Upload, Video, Loader2, CheckCircle, AlertCircle, ArrowLeft, FilePenLine, Plus, X, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useLocation } from "react-router-dom";
import { API_ROOT } from "@/config";
import BriefingUploader from "./briefing-uploader";
import AnalysisSidebar from "@/components/dashboard";
import CollapsedProfile from "@/components/ui/collapsed-profile";
import SEO from "@/components/seo";

// Helper to get the correct path based on user type
const getBasePath = () => {
  const userType = localStorage.getItem("userType");
  return userType === 'brand' ? '/brand' : '';
};

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface ExtractedBriefingData {
  campaign_name: string;
  brand_name: string;
  product_name: string;
  hooks: string[];
  hooks_must_be_present: boolean;
  topics: string[];
  topics_must_be_present: boolean;
  call_to_actions: string[];
  call_to_actions_must_be_present: boolean;
  elements: string[];
  elements_must_be_present: boolean;
  story: string;
  story_must_be_present: boolean;
  visuals_background: string;
  visuals_background_must_be_present: boolean;
  brand_logo_path?: string;
  brand_logo_must_be_present: boolean;
  product_image_path?: string;
  product_image_must_be_present: boolean;
}

const FoldedSidebar = ({ onShowSidebar, onNewAnalysisClick }: { onShowSidebar: () => void; onNewAnalysisClick: (e: React.MouseEvent) => void; }) => {
  return (
    <div
      className="h-full flex flex-col"
      style={{ 
        background: PALETTE.brightSnow, 
        width: 56, 
        borderRight: `1px solid ${PALETTE.americanSilver}` 
      }}
    >
      {/* Fixed Top Section */}
      <div className="flex-shrink-0">
        {/* Header with logo */}
        <div className="flex items-center justify-center px-4 py-3">
          <div
            onClick={onShowSidebar}
            title="Expand Sidebar"
            className="cursor-pointer"
          >
            <img 
              src="/brandcomply_logo.png" 
              alt="brandcomply logo" 
              className="h-6 w-6"
            />
          </div>
        </div>

        {/* New Analysis Button */}
        <div
          onClick={onNewAnalysisClick}
          className="flex items-center justify-center mx-4 my-3 px-3 py-2 rounded-lg cursor-pointer transition-all hover:opacity-80"
          style={{ 
            background: PALETTE.inchworm,
            color: PALETTE.gunmetal
          }}
          title="New Check"
        >
          <FilePenLine className="w-4 h-4 flex-shrink-0" />
        </div>
      </div>

      {/* Spacer to push profile to bottom */}
      <div className="flex-1"></div>

      {/* Fixed Bottom Section - Profile */}
      <div className="flex-shrink-0">
        <div className="flex justify-center p-2">
          <CollapsedProfile />
        </div>
      </div>
    </div>
  );
};

const BriefingVideoUploader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<'briefing' | 'campaign_details' | 'video'>('briefing');
  const [briefingData, setBriefingData] = useState<ExtractedBriefingData | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStage, setAnalysisStage] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState<{ category: string; index: number; value: string } | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<string | null>(null);
  
  // Sidebar state (no projects in simplified backend)
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("sidebarOpen");
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Save sidebar state
  useEffect(() => {
    localStorage.setItem("sidebarOpen", JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // Check for reused briefing data on mount
  useEffect(() => {
    const reusedBriefingData = localStorage.getItem('reuseBriefingData');
    const reusedBriefingTimestamp = localStorage.getItem('reuseBriefingTimestamp');
    
    if (reusedBriefingData && reusedBriefingTimestamp) {
      const timestamp = parseInt(reusedBriefingTimestamp);
      const now = Date.now();
      
      // Only use if the briefing data is recent (within last 5 minutes)
      if (now - timestamp < 300000) {
        try {
          const parsedData = JSON.parse(reusedBriefingData);
          console.log("🔄 Reusing briefing data from previous analysis");
          setBriefingData(parsedData);
          setStep('campaign_details');
          
          // Clean up localStorage
          localStorage.removeItem('reuseBriefingData');
          localStorage.removeItem('reuseBriefingTimestamp');
        } catch (error) {
          console.error("Error parsing reused briefing data:", error);
          // Clean up corrupted data
          localStorage.removeItem('reuseBriefingData');
          localStorage.removeItem('reuseBriefingTimestamp');
        }
      } else {
        // Clean up old data
        localStorage.removeItem('reuseBriefingData');
        localStorage.removeItem('reuseBriefingTimestamp');
      }
    }
  }, []); // Run once on mount

  const handleBriefingExtracted = (data: ExtractedBriefingData) => {
    setBriefingData(data);
    setStep('campaign_details');
  };

  const handleNewAnalysis = (e: React.MouseEvent) => {
    e.preventDefault();
    // Reset to briefing step
    setStep('briefing');
    setBriefingData(null);
    setSelectedVideo(null);
    setError(null);
    setAnalysisProgress(0);
    setAnalysisStage('');
    setIsAnalyzing(false);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, category: string, index: number, value: string) => {
    setDraggedItem({ category, index, value });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ''); // Required for Firefox
  };

  const handleDragOver = (e: React.DragEvent, category: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCategory(category);
  };

  const handleDragLeave = () => {
    setDragOverCategory(null);
  };

  const handleDrop = (e: React.DragEvent, targetCategory: string) => {
    e.preventDefault();
    setDragOverCategory(null);

    if (!draggedItem || !briefingData) return;

    const { category: sourceCategory, index: sourceIndex, value } = draggedItem;

    // Don't do anything if dropping on the same category
    if (sourceCategory === targetCategory) {
      setDraggedItem(null);
      return;
    }

    // Only allow dragging between array categories
    const arrayCategories = ['hooks', 'topics', 'call_to_actions', 'elements'];
    if (!arrayCategories.includes(sourceCategory) || !arrayCategories.includes(targetCategory)) {
      setDraggedItem(null);
      return;
    }

    // Remove from source category
    let sourceArray: string[] = [];
    if (sourceCategory === 'hooks') sourceArray = [...briefingData.hooks];
    else if (sourceCategory === 'topics') sourceArray = [...briefingData.topics];
    else if (sourceCategory === 'call_to_actions') sourceArray = [...briefingData.call_to_actions];
    else if (sourceCategory === 'elements') sourceArray = [...briefingData.elements];
    
    sourceArray.splice(sourceIndex, 1);

    // Add to target category
    let targetArray: string[] = [];
    if (targetCategory === 'hooks') targetArray = [...briefingData.hooks];
    else if (targetCategory === 'topics') targetArray = [...briefingData.topics];
    else if (targetCategory === 'call_to_actions') targetArray = [...briefingData.call_to_actions];
    else if (targetCategory === 'elements') targetArray = [...briefingData.elements];
    
    targetArray.push(value);

    // Update state
    const updates: Partial<ExtractedBriefingData> = {};
    if (sourceCategory === 'hooks') updates.hooks = sourceArray;
    else if (sourceCategory === 'topics') updates.topics = sourceArray;
    else if (sourceCategory === 'call_to_actions') updates.call_to_actions = sourceArray;
    else if (sourceCategory === 'elements') updates.elements = sourceArray;

    if (targetCategory === 'hooks') updates.hooks = targetArray;
    else if (targetCategory === 'topics') updates.topics = targetArray;
    else if (targetCategory === 'call_to_actions') updates.call_to_actions = targetArray;
    else if (targetCategory === 'elements') updates.elements = targetArray;

    setBriefingData({
      ...briefingData,
      ...updates,
    });

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverCategory(null);
  };

  const handleVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith('video/')) {
        setSelectedVideo(file);
        setError(null);
      } else {
        setError('Please select a video file');
        setSelectedVideo(null);
      }
    }
  };

  const handleAnalyze = async () => {
    if (!selectedVideo || !briefingData) return;

    setIsAnalyzing(true);
    setError(null);
    setAnalysisProgress(0);
    setAnalysisStage('Preparing video...');

    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const token = localStorage.getItem('token');

    // Start SSE connection for progress
    const eventSource = new EventSource(
      `${API_ROOT}/analysis-progress?session_id=${sessionId}&token=${token}`
    );

    eventSource.onmessage = (event) => {
      try {
        // Handle SSE data format - remove "data: " prefix if present
        let dataStr = event.data;
        if (dataStr.startsWith('data: ')) {
          dataStr = dataStr.substring(6); // Remove "data: " prefix
        }
        
        // Parse JSON
        const data = JSON.parse(dataStr);
        
        if (data.type === 'progress') {
          const progress = typeof data.progress === 'number' ? data.progress : parseFloat(data.progress);
          if (!isNaN(progress)) {
            setAnalysisProgress(Math.min(100, Math.max(0, progress)));
            
            // Set stage based on progress
            if (progress < 20) {
              setAnalysisStage('Preparing video...');
            } else if (progress < 40) {
              setAnalysisStage('Uploading video...');
            } else if (progress < 60) {
              setAnalysisStage('Processing video...');
            } else if (progress < 80) {
              setAnalysisStage('Analyzing compliance...');
            } else if (progress < 95) {
              setAnalysisStage('Generating report...');
            } else {
              setAnalysisStage('Finalizing...');
            }
          }
        } else if (data.type === 'complete') {
          setAnalysisProgress(100);
          setAnalysisStage('Complete!');
          eventSource.close();
        } else if (data.type === 'error') {
          setError(data.message || 'Analysis failed');
          eventSource.close();
          setIsAnalyzing(false);
          setAnalysisProgress(0);
          setAnalysisStage('');
        } else if (data.type === 'connected') {
          // Connection established, no action needed
          console.log('SSE connection established');
        }
      } catch (err) {
        console.error('Error parsing SSE message:', err);
        console.error('Raw event data:', event.data);
        // Don't break the connection on parse errors, just log them
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      // Only close if we're not already analyzing (might be a temporary network issue)
      // The connection will retry automatically
      if (!isAnalyzing) {
        eventSource.close();
      }
    };

    try {
      const formData = new FormData();
      formData.append('file', selectedVideo);
      formData.append('input_data', JSON.stringify(briefingData));
      formData.append('session_id', sessionId);

      const response = await fetch(`${API_ROOT}/analyze-video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Analysis failed');
      }

      const result = await response.json();
      eventSource.close();
      
      // Set flag to refresh sidebar when user returns
      localStorage.setItem("refreshSidebar", "true");
      localStorage.setItem("refreshSidebarTimestamp", Date.now().toString());
      console.log("🔄 REFRESH FLAG: Set sidebar refresh flag for new analysis");
      
      // Navigate to results page
      if (result?.unique_id) {
        navigate(`${getBasePath()}/result/${result.unique_id}`);
      } else {
        throw new Error("Analysis completed but no unique_id returned");
      }
    } catch (err) {
      eventSource.close();
      setError(err instanceof Error ? err.message : 'Analysis failed');
      setIsAnalyzing(false);
      setAnalysisProgress(0);
      setAnalysisStage('');
    }
  };

  return (
    <div className="flex h-screen" style={{ background: PALETTE.brightSnow }}>
      <SEO
        title="Upload Briefing"
        description="Upload your campaign briefing to BrandComply. Define brand guidelines for creators to self-check their videos."
        path="/upload-briefing"
      />
      {/* Sidebar */}
      {sidebarOpen ? (
        <AnalysisSidebar
          onHideSidebar={() => setSidebarOpen(false)}
        />
      ) : (
        <FoldedSidebar
          onShowSidebar={() => setSidebarOpen(true)}
          onNewAnalysisClick={handleNewAnalysis}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header style={{ background: PALETTE.gunmetal }} className="text-white shadow-lg flex-shrink-0">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                  brandcomply™
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Scrollable Content Area */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Progress Indicator - 4 Steps */}
        <div className="mb-8 w-full flex justify-center">
          <div className="flex items-center justify-center space-x-2 md:space-x-4 max-w-4xl w-full">
            {/* Step 1: Upload Briefing */}
            <div className="flex items-center space-x-2">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                style={{
                  background: step === 'briefing' && !briefingData ? PALETTE.inchworm : briefingData ? PALETTE.inchworm : PALETTE.americanSilver,
                  color: step === 'briefing' && !briefingData ? PALETTE.gunmetal : briefingData ? PALETTE.gunmetal : PALETTE.gunmetal
                }}
              >
                1
              </div>
              <span 
                className="text-xs md:text-sm font-semibold hidden sm:inline" 
                style={{ 
                  color: PALETTE.gunmetal,
                  maxWidth: '100px',
                  lineHeight: '1.3',
                  display: 'inline-block',
                  textAlign: 'left'
                }}
              >
                Briefing Upload
              </span>
            </div>
            <div className="w-8 md:w-12 h-0.5 flex-shrink-0" style={{ background: (step === 'campaign_details' || step === 'video') ? PALETTE.inchworm : PALETTE.americanSilver }} />
            
            {/* Step 2: Briefing Review */}
            <div className="flex items-center space-x-2">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                style={{
                  background: (step === 'campaign_details' || step === 'video') ? PALETTE.inchworm : PALETTE.americanSilver,
                  color: PALETTE.gunmetal
                }}
              >
                2
              </div>
              <span 
                className="text-xs md:text-sm font-semibold hidden sm:inline" 
                style={{ 
                  color: PALETTE.gunmetal,
                  maxWidth: '100px',
                  lineHeight: '1.3',
                  display: 'inline-block',
                  textAlign: 'left'
                }}
              >
                Briefing Review
              </span>
            </div>
            <div className="w-8 md:w-12 h-0.5 flex-shrink-0" style={{ background: step === 'video' ? PALETTE.inchworm : PALETTE.americanSilver }} />
            
            {/* Step 3: Upload Video */}
            <div className="flex items-center space-x-2">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                style={{
                  background: step === 'video' ? PALETTE.inchworm : PALETTE.americanSilver,
                  color: PALETTE.gunmetal
                }}
              >
                3
              </div>
              <span 
                className="text-xs md:text-sm font-semibold hidden sm:inline" 
                style={{ 
                  color: PALETTE.gunmetal,
                  maxWidth: '100px',
                  lineHeight: '1.3',
                  display: 'inline-block',
                  textAlign: 'left'
                }}
              >
                Video Upload
              </span>
            </div>
            <div className="w-8 md:w-12 h-0.5 flex-shrink-0" style={{ background: step === 'video' ? PALETTE.inchworm : PALETTE.americanSilver }} />
            
            {/* Step 4: Compliance Check & Recommendations */}
            <div className="flex items-center space-x-2">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                style={{
                  background: PALETTE.americanSilver,
                  color: PALETTE.gunmetal
                }}
              >
                4
              </div>
              <span 
                className="text-xs md:text-sm font-semibold hidden sm:inline" 
                style={{ 
                  color: PALETTE.gunmetal,
                  maxWidth: '100px',
                  lineHeight: '1.3',
                  display: 'inline-block',
                  textAlign: 'left'
                }}
              >
                Compliance Check & Recommendations
              </span>
            </div>
          </div>
        </div>

        {/* Step 1: Briefing Upload */}
        {step === 'briefing' && (
          <BriefingUploader onBriefingExtracted={handleBriefingExtracted} />
        )}

        {/* Step 2: Review & Edit Campaign Details */}
        {step === 'campaign_details' && briefingData && (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              {/* Success Banner */}
              <div className="mb-6 p-4 rounded-lg flex items-center space-x-3" style={{ background: `${PALETTE.inchworm}15`, border: `1px solid ${PALETTE.inchworm}30` }}>
                <CheckCircle className="w-6 h-6 flex-shrink-0" style={{ color: PALETTE.inchworm }} />
                <div>
                  <h3 className="text-base font-semibold mb-1" style={{ color: PALETTE.gunmetal }}>
                    Briefing Extracted Successfully
                  </h3>
                  <p className="text-sm" style={{ color: `${PALETTE.gunmetal}80` }}>
                    Review and edit the extracted information below, or accept it as-is to continue.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
                  Review & Edit Campaign Details
                </h2>
                <p className="text-base mb-4" style={{ color: `${PALETTE.gunmetal}70` }}>
                  Verify the extracted information and make any necessary changes before proceeding.
                </p>
              </div>

              <div className="space-y-6">
                {/* Campaign Name */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                    Campaign Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={briefingData.campaign_name}
                    onChange={(e) => setBriefingData({ ...briefingData, campaign_name: e.target.value })}
                    placeholder="Enter campaign name"
                    className="w-full"
                  />
                </div>

                {/* Brand Name */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                    Brand Name
                  </label>
                  <Input
                    value={briefingData.brand_name}
                    onChange={(e) => setBriefingData({ ...briefingData, brand_name: e.target.value })}
                    placeholder="Enter brand name"
                    className="w-full"
                  />
                </div>

                {/* Product Name */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                    Product Name
                  </label>
                  <Input
                    value={briefingData.product_name}
                    onChange={(e) => setBriefingData({ ...briefingData, product_name: e.target.value })}
                    placeholder="Enter product name"
                    className="w-full"
                  />
                </div>

                {/* Hooks */}
                <div
                  onDragOver={(e) => handleDragOver(e, 'hooks')}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, 'hooks')}
                  className={`transition-all rounded-lg p-2 ${dragOverCategory === 'hooks' ? 'bg-green-50 border-2 border-dashed border-green-400' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Hooks
                    </label>
                  </div>
                  <div className="space-y-2">
                    {briefingData.hooks.map((hook, idx) => (
                      <div
                        key={idx}
                        draggable
                        onDragStart={(e) => handleDragStart(e, 'hooks', idx, hook)}
                        onDragEnd={handleDragEnd}
                        className="flex items-center space-x-2 cursor-move hover:bg-gray-50 p-2 rounded transition-colors"
                      >
                        <div className="flex-1 flex items-center space-x-2">
                          <div className="text-gray-400 cursor-grab active:cursor-grabbing">⋮⋮</div>
                          <Input
                            value={hook}
                            onChange={(e) => {
                              const newHooks = [...briefingData.hooks];
                              newHooks[idx] = e.target.value;
                              setBriefingData({ ...briefingData, hooks: newHooks });
                            }}
                            placeholder="Hook text"
                            className="flex-1"
                            onDragStart={(e) => e.stopPropagation()}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newHooks = briefingData.hooks.filter((_, i) => i !== idx);
                            setBriefingData({ ...briefingData, hooks: newHooks });
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBriefingData({ ...briefingData, hooks: [...briefingData.hooks, ''] })}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Hook
                    </Button>
                  </div>
                </div>

                {/* Topics */}
                <div
                  onDragOver={(e) => handleDragOver(e, 'topics')}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, 'topics')}
                  className={`transition-all rounded-lg p-2 ${dragOverCategory === 'topics' ? 'bg-green-50 border-2 border-dashed border-green-400' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Topics 
                    </label>
                  </div>
                  <div className="space-y-2">
                    {briefingData.topics.map((topic, idx) => (
                      <div
                        key={idx}
                        draggable
                        onDragStart={(e) => handleDragStart(e, 'topics', idx, topic)}
                        onDragEnd={handleDragEnd}
                        className="flex items-center space-x-2 cursor-move hover:bg-gray-50 p-2 rounded transition-colors"
                      >
                        <div className="flex-1 flex items-center space-x-2">
                          <div className="text-gray-400 cursor-grab active:cursor-grabbing">⋮⋮</div>
                          <Input
                            value={topic}
                            onChange={(e) => {
                              const newTopics = [...briefingData.topics];
                              newTopics[idx] = e.target.value;
                              setBriefingData({ ...briefingData, topics: newTopics });
                            }}
                            placeholder="Topic"
                            className="flex-1"
                            onDragStart={(e) => e.stopPropagation()}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newTopics = briefingData.topics.filter((_, i) => i !== idx);
                            setBriefingData({ ...briefingData, topics: newTopics });
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBriefingData({ ...briefingData, topics: [...briefingData.topics, ''] })}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Topic
                    </Button>
                  </div>
                </div>

                {/* Call to Actions */}
                <div
                  onDragOver={(e) => handleDragOver(e, 'call_to_actions')}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, 'call_to_actions')}
                  className={`transition-all rounded-lg p-2 ${dragOverCategory === 'call_to_actions' ? 'bg-green-50 border-2 border-dashed border-green-400' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Call to Actions
                    </label>
                  </div>
                  <div className="space-y-2">
                    {briefingData.call_to_actions.map((cta, idx) => (
                      <div
                        key={idx}
                        draggable
                        onDragStart={(e) => handleDragStart(e, 'call_to_actions', idx, cta)}
                        onDragEnd={handleDragEnd}
                        className="flex items-center space-x-2 cursor-move hover:bg-gray-50 p-2 rounded transition-colors"
                      >
                        <div className="flex-1 flex items-center space-x-2">
                          <div className="text-gray-400 cursor-grab active:cursor-grabbing">⋮⋮</div>
                          <Input
                            value={cta}
                            onChange={(e) => {
                              const newCTAs = [...briefingData.call_to_actions];
                              newCTAs[idx] = e.target.value;
                              setBriefingData({ ...briefingData, call_to_actions: newCTAs });
                            }}
                            placeholder="CTA text"
                            className="flex-1"
                            onDragStart={(e) => e.stopPropagation()}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newCTAs = briefingData.call_to_actions.filter((_, i) => i !== idx);
                            setBriefingData({ ...briefingData, call_to_actions: newCTAs });
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBriefingData({ ...briefingData, call_to_actions: [...briefingData.call_to_actions, ''] })}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add CTA
                    </Button>
                  </div>
                </div>

                {/* Additional Elements */}
                <div
                  onDragOver={(e) => handleDragOver(e, 'elements')}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, 'elements')}
                  className={`transition-all rounded-lg p-2 ${dragOverCategory === 'elements' ? 'bg-green-50 border-2 border-dashed border-green-400' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      DON'Ts
                    </label>
                  </div>
                  <div className="space-y-2">
                    {briefingData.elements.map((element, idx) => (
                      <div
                        key={idx}
                        draggable
                        onDragStart={(e) => handleDragStart(e, 'elements', idx, element)}
                        onDragEnd={handleDragEnd}
                        className="flex items-center space-x-2 cursor-move hover:bg-gray-50 p-2 rounded transition-colors"
                      >
                        <div className="flex-1 flex items-center space-x-2">
                          <div className="text-gray-400 cursor-grab active:cursor-grabbing">⋮⋮</div>
                          <Input
                            value={element}
                            onChange={(e) => {
                              const newElements = [...briefingData.elements];
                              newElements[idx] = e.target.value;
                              setBriefingData({ ...briefingData, elements: newElements });
                            }}
                            placeholder="Element"
                            className="flex-1"
                            onDragStart={(e) => e.stopPropagation()}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newElements = briefingData.elements.filter((_, i) => i !== idx);
                            setBriefingData({ ...briefingData, elements: newElements });
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBriefingData({ ...briefingData, elements: [...briefingData.elements, ''] })}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add DON'T
                    </Button>
                  </div>
                </div>

                {/* Story */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Story 
                    </label>
                  </div>
                  <Textarea
                    value={briefingData.story}
                    onChange={(e) => setBriefingData({ ...briefingData, story: e.target.value })}
                    placeholder="Describe the story or narrative to be told..."
                    className="w-full min-h-[100px]"
                  />
                </div>

                {/* Visuals & Background */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Visuals & Background
                    </label>
                  </div>
                  <Textarea
                    value={briefingData.visuals_background}
                    onChange={(e) => setBriefingData({ ...briefingData, visuals_background: e.target.value })}
                    placeholder="Describe visual style, setting, or background requirements..."
                    className="w-full min-h-[100px]"
                  />
                </div>

                {/* Brand Logo */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Brand Logo URL (Optional)
                    </label>
                  </div>
                  <Input
                    value={briefingData.brand_logo_path || ''}
                    onChange={(e) => {
                      const url = e.target.value.trim();
                      setBriefingData({ 
                        ...briefingData, 
                        brand_logo_path: url,
                        // Auto-set must_be_present to true if URL is provided, false if cleared
                        brand_logo_must_be_present: url.length > 0
                      });
                    }}
                    placeholder="https://example.com/logo.png"
                    className="w-full"
                  />
                </div>

                {/* Product Image */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                      Product Image URL (Optional)
                    </label>
                  </div>
                  <Input
                    value={briefingData.product_image_path || ''}
                    onChange={(e) => {
                      const url = e.target.value.trim();
                      setBriefingData({ 
                        ...briefingData, 
                        product_image_path: url,
                        // Auto-set must_be_present to true if URL is provided, false if cleared
                        product_image_must_be_present: url.length > 0
                      });
                    }}
                    placeholder="https://example.com/product.png"
                    className="w-full"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4 mt-8">
                <Button
                  onClick={() => {
                    setStep('briefing');
                    setBriefingData(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Briefing
                </Button>
                <Button
                  onClick={() => setStep('video')}
                  disabled={!briefingData.campaign_name.trim()}
                  className="flex-1"
                  style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm & Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Video Upload */}
        {step === 'video' && briefingData && (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2" style={{ color: PALETTE.gunmetal }}>
                  Upload Video for Analysis
                </h2>
                <p className="text-base" style={{ color: `${PALETTE.gunmetal}70` }}>
                  Campaign: <strong>{briefingData.campaign_name}</strong>
                </p>
              </div>

              {!isAnalyzing ? (
                <>
                  <div
                    className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:bg-gray-50 transition-colors mb-6"
                    style={{ borderColor: PALETTE.americanSilver }}
                    onClick={() => document.getElementById('video-file-input')?.click()}
                  >
                    <input
                      id="video-file-input"
                      type="file"
                      accept="video/*"
                      onChange={handleVideoSelect}
                      className="hidden"
                    />
                    <Video className="w-16 h-16 mx-auto mb-4" style={{ color: PALETTE.gunmetal }} />
                    <p className="text-lg font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                      {selectedVideo ? selectedVideo.name : 'Click to upload video'}
                    </p>
                    <p className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                      Supports MP4, MOV, AVI, and other video formats
                    </p>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 rounded-lg flex items-start space-x-3" style={{ background: `${PALETTE.orange}10` }}>
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: PALETTE.orange }} />
                      <p className="text-sm" style={{ color: PALETTE.orange }}>{error}</p>
                    </div>
                  )}

                  <div className="flex space-x-4">
                    <Button
                      onClick={() => {
                        setStep('campaign_details');
                        setSelectedVideo(null);
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Campaign Details
                    </Button>
                    <Button
                      onClick={handleAnalyze}
                      disabled={!selectedVideo}
                      className="flex-1"
                      style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Analyze Video
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-lg font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                    {analysisStage || 'Analyzing video...'}
                  </p>
                  {/* Progress bar */}
                  <div className="w-full max-w-md">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                        {Math.round(analysisProgress)}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: `${PALETTE.americanSilver}20` }}>
                      <div
                        className="h-full rounded-full transition-all duration-300 ease-out"
                        style={{
                          width: `${analysisProgress}%`,
                          background: PALETTE.inchworm,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default BriefingVideoUploader;

